import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/steps";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LayoutDashboard, Workflow, PlusCircle, Bell, Users, LogOut, Building2, GraduationCap, Layers, Server, Package, Menu, BarChart3 } from "lucide-react";
import { cn } from "@/lib/utils";

const UNREAD_POLL_MS = 30000;
// Notifications.jsx dispatches this right after marking item(s) read, so the sidebar
// badge updates immediately instead of waiting for the next poll or a route change.
export const NOTIFICATIONS_UPDATED_EVENT = "trackly:notifications-updated";

// Polls the unread notification count so the sidebar badge stays current without
// requiring a full page reload, and refreshes immediately on route change (e.g.
// right after visiting /notifications) or on the NOTIFICATIONS_UPDATED_EVENT.
function useUnreadCount() {
  const [count, setCount] = useState(0);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications", { params: { unread_only: true } });
        if (!cancelled) setCount(data.length);
      } catch {
        // Non-critical — leave the last known count in place.
      }
    };
    load();
    const interval = setInterval(load, UNREAD_POLL_MS);
    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, load);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, load);
    };
  }, [location.pathname]);

  return count;
}

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/programs", label: "Programs", icon: Workflow, testid: "nav-programs" },
  { to: "/programs/new", label: "New Program", icon: PlusCircle, roles: ["bd", "admin"], testid: "nav-new-program" },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["bd", "admin"], testid: "nav-reports" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
  { to: "/admin/users", label: "Users", icon: Users, roles: ["admin"], testid: "nav-users" },
  { to: "/admin/clients", label: "Clients", icon: Building2, roles: ["admin", "bd"], testid: "nav-clients" },
  { to: "/admin/faculty", label: "Faculty", icon: GraduationCap, roles: ["admin", "pm"], testid: "nav-faculty" },
  { to: "/admin/skills", label: "Skills", icon: Layers, roles: ["admin"], testid: "nav-skills" },
  { to: "/admin/infra", label: "Infra Catalog", icon: Server, roles: ["admin"], testid: "nav-infra" },
  { to: "/admin/software", label: "Software Catalog", icon: Package, roles: ["admin"], testid: "nav-software" },
];

function SidebarContent({ user, onLogout, onNavigate, idSuffix = "", unreadCount = 0 }) {
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex h-full flex-col">
      <div className="px-6 py-6 border-b border-slate-200/80">
        <div className="flex items-center justify-between gap-2.5">
          <div className="flex items-center gap-2.5">
            <img src="/assets/nexusiq-logo.png" alt="NexusIQ Solutions" className="h-7 w-auto object-contain" />
            <div>
              <div className="font-heading font-semibold text-[15px] leading-none tracking-tight text-slate-900">Trackly</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">NexusIQ Solutions</div>
            </div>
          </div>
          <Button variant="ghost" size="icon" data-testid={`logout-btn${idSuffix}`} onClick={onLogout} aria-label="Logout">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.filter((n) => !n.roles || (user?.roles || []).includes("admin") || n.roles.some((r) => (user?.roles || []).includes(r))).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={`${n.testid}${idSuffix}`}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 px-3 py-[7px] rounded-md text-[13px] font-medium transition-all duration-150",
                isActive
                  ? "bg-primary/[0.08] text-primary shadow-well border border-primary/10"
                  : "text-slate-600 border border-transparent hover:bg-white hover:shadow-raised-sm hover:text-slate-900"
              )
            }
          >
            <n.icon className="w-[15px] h-[15px] shrink-0" />
            <span className="flex-1">{n.label}</span>
            {n.to === "/notifications" && unreadCount > 0 && (
              <span
                data-testid={`nav-notifications-badge${idSuffix}`}
                className="min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold grid place-items-center leading-none"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-slate-200/80">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="h-8 w-8 ring-1 ring-slate-200">
            <AvatarFallback className="bg-primary/8 text-primary text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium truncate text-slate-900">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{(user?.roles || []).map((r) => ROLE_LABELS[r]).join(" · ")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const unreadCount = useUnreadCount();

  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-background text-foreground lg:flex">
      <aside className="hidden lg:flex w-64 shrink-0 skeu-chrome flex-col relative z-10">
        <SidebarContent user={user} onLogout={doLogout} unreadCount={unreadCount} />
      </aside>

      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200/80 skeu-chrome sticky top-0 z-20 lg:hidden">
        <div className="flex items-center gap-2.5">
          <img src="/assets/nexusiq-logo.png" alt="NexusIQ Solutions" className="h-7 w-auto object-contain" />
          <div className="font-heading font-semibold text-[15px] leading-none tracking-tight text-slate-900">Trackly</div>
        </div>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Open menu" data-testid="mobile-nav-trigger">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 max-w-[80vw] p-0 skeu-chrome">
            <SidebarContent user={user} onLogout={doLogout} onNavigate={() => setMobileOpen(false)} idSuffix="-mobile" unreadCount={unreadCount} />
          </SheetContent>
        </Sheet>
      </header>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        {children}
      </main>
    </div>
  );
}
